// ============================================================================
// MODULE HEADER — Standardized header for ALL modules
//
// Three variants:
//   1. "dashboard"   — Double-height: title bar + type filter tabs + stat cards
//                      Used by dashboards (ComposableDashboard, etc.).
//                      Keeps the stat-cards row visible in vertical-nav mode —
//                      dashboards never render secondary pill rows, so the
//                      freed space hosts the stats instead.
//   2. "compact"     — Single-height: title bar only (+ optional type filter tabs)
//                      Chevron minimizes entire header. Used by DynamicOperationalView
//                      and EmailTasksManagement when no stat cards are needed.
//   3. "with-stats"  — Medium-height: title bar + stat cards below
//                      Chevron hides stat cards but keeps title bar.
//                      Used by DynamicOperationalView when stat cards are shown.
//
// Design rules:
//   - NO margin or padding left/top/right — edge-to-edge.
//   - Square corners (no rounded).
//   - Flush against content area boundary.
//
// Date: February 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';

import type { ReactNode } from 'react';

import { ChevronUp, ChevronDown, RefreshCw, Settings, ExternalLink, Plus, Check, ArrowUpLeft, ArrowDownRight, Star, PanelTop, PanelLeft, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SHELL_TOGGLE_BUTTON_CLASS, SHELL_TOGGLE_ICON_CLASS } from '@/components/shared/shellToggleButtonStyles';
import { useFavourites } from '@/hooks/useFavourites';
import { useModuleColor } from '@/hooks/useModuleColor';
import { useClearRedirectParams } from '@/hooks/useClearRedirectParams';

const LAYOUT_PREFS_KEY = 'ta_ui_layout_prefs';
const LAYOUT_PREFS_EVENT = 'ta-ui-layout-prefs-changed';

type LayoutPrefs = Record<string, unknown>;

function readLayoutPrefs(): LayoutPrefs {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    return JSON.parse(localStorage.getItem(LAYOUT_PREFS_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeLayoutPrefs(updates: LayoutPrefs) {
  if (typeof window === 'undefined') {
    return;
  }

  const nextPrefs = {
    ...readLayoutPrefs(),
    ...updates,
  };

  localStorage.setItem(LAYOUT_PREFS_KEY, JSON.stringify(nextPrefs));
  window.dispatchEvent(new CustomEvent(LAYOUT_PREFS_EVENT, {
    detail: {
      changedKeys: Object.keys(updates),
      prefs: nextPrefs,
    },
  }));
}

// ============================================================================
// TYPES
// ============================================================================

export type ModuleHeaderVariant = 'dashboard' | 'compact' | 'with-stats';

export interface HeaderAction {
  id?: string;
  icon: React.ElementType;
  label?: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'ghost' | 'solid' | 'default' | 'primary' | 'secondary';   // `primary` maps to solid, others map to default/ghost styles
  showLabel?: 'always' | 'expanded' | 'never';
  title?: string;
  /** Tour spotlight target id */
  dataTourId?: string;
}

export interface StatPill {
  id: string;
  label: string;
  value: number | string;
  icon?: React.ElementType;
  /** Tailwind gradient — e.g. 'from-blue-500 to-indigo-500' */
  gradient?: string;
  /** For non-gradient pills, provide text/border/bg classes */
  className?: string;
  onClick?: () => void;
  /** Only render when value is truthy (> 0 for numbers) */
  hideWhenZero?: boolean;
}

export interface StatCard {
  id: string;
  label: string;
  value: number | string;
  icon?: React.ElementType;
  /** Card background colour */
  color?: string;
  /** Additional subtitle beneath the value */
  subtitle?: string;
  onClick?: () => void;
  active?: boolean;
  /** Tour spotlight target id */
  dataTourId?: string;
}

export interface TypeFilterTab {
  id: string;
  label: string;
  icon?: React.ElementType;
  highlighted?: boolean;
  dataTourId?: string;
  /** When true, a thin vertical divider is rendered before this tab */
  separatorBefore?: boolean;
  /** Subtle dot/underline accent color for this tab (hex). Used for per-tab identity within a group. */
  accentColor?: string;
  /** Optional status badge text (e.g. "3 pending") rendered on the tab/pill. */
  badge?: string;
  badgeColor?: 'red' | 'blue' | 'green' | 'amber' | 'gray';
}

export interface ModuleHeaderProps {
  // ---- Variant ----
  variant?: ModuleHeaderVariant;

  // ---- Density ----
  density?: 'default' | 'compact';

  // ---- Core content ----
  icon: React.ElementType;
  title: string;
  subtitle?: string;
  /** Optional node rendered beside the title (e.g. UserDocsLink). */
  titleExtra?: React.ReactNode;

  // ---- Styling ----
  moduleKey?: string;
  primaryColor?: string;
  backgroundClassName?: string;

  // ---- Type filter tabs (center area — used by dashboard & compact) ----
  typeFilterTabs?: TypeFilterTab[];
  /** Optional second pill-bar rendered below the title row. Useful when there are too many
   *  tabs to fit inline (e.g. Sales documents split into primary vs. supporting).
   *  Uses activeTypeFilterSecondary/onTypeFilterSecondaryChange when provided, otherwise
   *  shares activeTypeFilter/onTypeFilterChange with the primary row. */
  typeFilterTabsSecondary?: TypeFilterTab[];
  activeTypeFilter?: string | null;
  onTypeFilterChange?: (id: string | null) => void;
  /** Active filter and change handler for the secondary pill bar (when independent from primary). */
  activeTypeFilterSecondary?: string | null;
  onTypeFilterSecondaryChange?: (id: string | null) => void;
  /** Show "All" tab before the type filters */
  showAllTab?: boolean;
  /** When true, render the secondary type filter row below the stat cards instead of above them. */
  secondaryFiltersBelowStats?: boolean;

  // ---- Center content (alternative to typeFilterTabs) ----
  centerContent?: ReactNode;

  // ---- Stat pills (displayed below type filters in "with-stats" & "dashboard") ----
  statPills?: StatPill[];

  // ---- Stat cards (dashboard variant — 6-card row beneath title) ----
  statCards?: StatCard[];

  // ---- View toggles (right side, below title in with-stats / compact) ----
  viewToggles?: ReactNode;

  // ---- Right-side actions ----
  actions?: HeaderAction[];

  // ---- Common action shortcuts ----
  showSyncButton?: boolean;
  onSync?: () => void;
  syncing?: boolean;

  showNewButton?: boolean;
  newButtonLabel?: string;
  onNew?: () => void;
  newButtonDataTourId?: string;

  // ---- Collapse behaviour ----
  storageKey?: string;
  defaultMinimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;

  // ---- Visibility ----
  hidden?: boolean;
  className?: string;

  // ---- Bottom slot (render anything below the header inside the coloured area) ----
  bottomSlot?: ReactNode;

  // ---- Persistent bottom slot (always visible, even when minimized) ----
  /** Renders below the collapsible section but still inside the coloured area.
   *  Stays visible when the header is minimized (dashboard / with-stats variants). */
  persistentBottomSlot?: ReactNode;

  // ---- Layout sync control ----
  /** Shows a bottom-left toggle that collapses/expands both the header and sidenav together. */
  showCombinedLayoutToggle?: boolean;

  // ---- Favourites ----
  /** When provided, shows a star button to add/remove this page from the user's favourites bar. */
  favouriteConfig?: {
    label: string;
    href: string;
    moduleKey?: string;
    section?: string;
  };

  // ---- Component Tour Icon ----
  /** Configuration for the component tour icon (bottom-right, above minimize toggle).
   *  When provided, shows a HelpCircle icon that opens an isolated tour for a specific component.
   *  If `onHelpClick` is provided on ModulePageShell, this button triggers the AI onboarding
   *  coach instead of the legacy guided-tour-open event. */
  componentTourConfig?: {
    /** The module key for the tour (e.g., 'total-finance', 'total-operations') */
    moduleKey: string;
    /** The flow ID for the main tour (e.g., 'inventory', 'workflow-inventory-workspace') */
    flowId: string;
    /** Optional: specific step ID to start at (e.g., 'items-section', 'inventory-section') */
    stepId?: string;
    /** Optional: objective ID to start at */
    objectiveId?: string;
    /** Tooltip/aria-label for the icon */
    tooltip?: string;
  };

  // ---- AI Onboarding Coach Help Button ----
  /** When provided, shows a HelpCircle icon (bottom-right, above minimize toggle)
   *  that triggers the AI onboarding coach for the current view. This takes
   *  precedence over componentTourConfig if both are provided. */
  onHelpClick?: () => void;
  /** Tooltip/aria-label for the help button. Defaults to "Get help with this section". */
  helpButtonTooltip?: string;

  // ---- One-shot redirect query params ----
  /** When provided, any user click on a type filter tab (primary or secondary)
   *  will strip these query params from the URL via `router.replace`. Used to
   *  consume one-shot deep-link redirects (e.g. `?tab=sales&secondaryTab=invoices`)
   *  so that a page reload no longer re-triggers the redirect. */
  clearQueryParamsOnChange?: string[];
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function ModuleHeader({
  variant = 'compact',
  density = 'default',
  icon: Icon,
  title,
  subtitle,
  titleExtra,
  moduleKey,
  primaryColor,
  backgroundClassName,
  typeFilterTabs,
  typeFilterTabsSecondary,
  activeTypeFilter,
  onTypeFilterChange,
  activeTypeFilterSecondary,
  onTypeFilterSecondaryChange,
  showAllTab = true,
  secondaryFiltersBelowStats = false,
  centerContent,
  statPills,
  statCards,
  viewToggles,
  actions = [],
  showSyncButton,
  onSync,
  syncing,
  showNewButton,
  newButtonLabel = 'New Item',
  onNew,
  newButtonDataTourId,
  storageKey = 'ta_module_header',
  defaultMinimized = false,
  onMinimizedChange,
  hidden = false,
  className,
  bottomSlot,
  persistentBottomSlot,
  showCombinedLayoutToggle = false,
  favouriteConfig,
  componentTourConfig,
  onHelpClick,
  helpButtonTooltip,
  clearQueryParamsOnChange,
}: ModuleHeaderProps) {
  // ---------- State ----------
  const [minimized, setMinimized] = useState<boolean>(() => {
    const storedValue = readLayoutPrefs()[storageKey];
    return typeof storedValue === 'boolean' ? storedValue : defaultMinimized;
  });
  const [sideNavCollapsed, setSideNavCollapsed] = useState(() => Boolean(readLayoutPrefs().sideNavCollapsed));
  const [navLayoutMode, setNavLayoutMode] = useState<'horizontal' | 'vertical'>(() => {
    const stored = readLayoutPrefs().navLayoutMode;
    return stored === 'vertical' ? 'vertical' : 'horizontal';
  });
  const { isFavourite, toggleFavourite, setBarVisible } = useFavourites();
  const { moduleColor } = useModuleColor(moduleKey ?? '', primaryColor || '');
  const isFav = favouriteConfig ? isFavourite(favouriteConfig.href, favouriteConfig.section) : false;
  const resolvedPrimaryColor = primaryColor || moduleColor || undefined;

  // ---------- One-shot redirect param clearing ----------
  // When `clearQueryParamsOnChange` is provided, wrap the type filter change
  // handlers so any user tab click strips the redirect params from the URL.
  const clearRedirectParams = useClearRedirectParams(clearQueryParamsOnChange ?? []);
  const handleTypeFilterChange = useCallback((id: string | null) => {
    clearRedirectParams();
    onTypeFilterChange?.(id);
  }, [clearRedirectParams, onTypeFilterChange]);
  const handleTypeFilterSecondaryChange = useCallback((id: string | null) => {
    clearRedirectParams();
    onTypeFilterSecondaryChange?.(id);
  }, [clearRedirectParams, onTypeFilterSecondaryChange]);

  // ---------- Persist ----------
  useEffect(() => {
    writeLayoutPrefs({ [storageKey]: minimized });
    onMinimizedChange?.(minimized);
  }, [minimized, storageKey, onMinimizedChange]);

  useEffect(() => {
    const syncFromPrefs = (prefs?: LayoutPrefs) => {
      const resolvedPrefs = prefs ?? readLayoutPrefs();
      const storedMinimized = resolvedPrefs[storageKey];
      const nextMinimized = typeof storedMinimized === 'boolean' ? storedMinimized : defaultMinimized;
      const nextSideNavCollapsed = Boolean(resolvedPrefs.sideNavCollapsed);
      const nextNavLayoutMode = resolvedPrefs.navLayoutMode === 'vertical' ? 'vertical' : 'horizontal';

      setMinimized((prev: boolean) => (prev === nextMinimized ? prev : nextMinimized));
      setSideNavCollapsed((prev: boolean) => (prev === nextSideNavCollapsed ? prev : nextSideNavCollapsed));
      setNavLayoutMode((prev: 'horizontal' | 'vertical') => (prev === nextNavLayoutMode ? prev : nextNavLayoutMode));
    };

    const handlePrefsChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ changedKeys?: string[]; prefs?: LayoutPrefs }>).detail;
      if (!detail?.changedKeys || detail.changedKeys.includes(storageKey) || detail.changedKeys.includes('sideNavCollapsed') || detail.changedKeys.includes('navLayoutMode')) {
        syncFromPrefs(detail?.prefs);
      }
    };

    const handleStorage = () => syncFromPrefs();

    window.addEventListener(LAYOUT_PREFS_EVENT, handlePrefsChanged as EventListener);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(LAYOUT_PREFS_EVENT, handlePrefsChanged as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, [defaultMinimized, storageKey]);

  const toggleMinimized = useCallback(() => {
    setMinimized((prev: boolean) => !prev);
  }, []);

  const toggleCombinedLayout = useCallback(() => {
    const allCollapsed = minimized && sideNavCollapsed;
    const nextCollapsed = !allCollapsed;

    setMinimized(nextCollapsed);
    setSideNavCollapsed(nextCollapsed);
    writeLayoutPrefs({
      [storageKey]: nextCollapsed,
      sideNavCollapsed: nextCollapsed,
    });
  }, [minimized, sideNavCollapsed, storageKey]);

  const toggleNavLayoutMode = useCallback(() => {
    const next = navLayoutMode === 'horizontal' ? 'vertical' : 'horizontal';
    writeLayoutPrefs({ navLayoutMode: next });
    setNavLayoutMode(next);
  }, [navLayoutMode]);

  const isVerticalNav = navLayoutMode === 'vertical';

  const handleToggleFavourite = useCallback(() => {
    if (!favouriteConfig) return;
    const added = toggleFavourite({
      label: favouriteConfig.label,
      href: favouriteConfig.href,
      moduleKey: favouriteConfig.moduleKey,
      section: favouriteConfig.section,
    });
    if (added) {
      setBarVisible(true);
    }
  }, [favouriteConfig, setBarVisible, toggleFavourite]);

  // ---------- Assemble actions ----------
  const allActions = useMemo<HeaderAction[]>(() => {
    const safeActions = Array.isArray(actions) ? actions : [];
    const list: HeaderAction[] = [...safeActions];
    if (showSyncButton && onSync) {
      list.push({
        id: '_sync',
        icon: RefreshCw,
        label: 'Refresh',
        onClick: onSync,
        loading: syncing,
        showLabel: 'never',
        title: 'Refresh',
      });
    }
    return list;
  }, [actions, showSyncButton, onSync, syncing]);

  // ---------- What is collapsible? ----------
  // dashboard  → chevron hides stat cards + stat pills (title bar stays)
  // compact    → chevron minimizes the entire coloured bar
  // with-stats → chevron hides stat pills / stat cards (title bar stays)

  const showStatPills = statPills && statPills.length > 0;
  const showStatCards = statCards && statCards.length > 0;
  const hasTypeFilters = typeFilterTabs && typeFilterTabs.length > 0;
  const hasSecondaryTypeFilters = !!(typeFilterTabsSecondary && typeFilterTabsSecondary.length > 0);
  const hasViewToggles = Boolean(viewToggles);
  const isDense = density === 'compact';
  const useCompactStatCards = isDense || (statCards?.length ?? 0) > 4;
  const statCardGridClass = useMemo(() => {
    const count = statCards?.length ?? 0;

    if (count <= 1) return 'lg:grid-cols-1';
    if (count === 2) return 'lg:grid-cols-2';
    if (count === 3) return 'lg:grid-cols-3';
    if (count === 4) return 'lg:grid-cols-4';
    if (count === 5) return 'lg:grid-cols-5';
    return 'lg:grid-cols-6';
  }, [statCards]);

  // For dashboard & with-stats, "minimized" hides the expandable section
  // For compact, "minimized" shrinks the title bar itself
  // When vertical nav is active, stat content is always collapsed (pills/cards moved to sidebar)
  const isCollapsedContent = (minimized && (variant === 'dashboard' || variant === 'with-stats')) || isVerticalNav;
  const isCompactMinimized = minimized && variant === 'compact';
  // Dashboard variant keeps its stat-cards row visible in vertical nav —
  // dashboards never render secondary pill rows, so the freed space hosts
  // the stats instead (mirrors StrictModuleHeader's dashboard variant).
  // The minimise chevron still hides the row.
  const showDashboardStatsInVerticalNav = variant === 'dashboard' && isVerticalNav && !minimized;

  // When vertical nav is active, show the active type filter label as the title
  // so the user knows which navigation item is current (pills are in the sidebar)
  const activeTypeFilterLabel = typeFilterTabs?.find(t => t.id === activeTypeFilter)?.label;
  const displayTitle = isVerticalNav && activeTypeFilterLabel ? activeTypeFilterLabel : title;

  if (hidden) return null;

  // ---------- Background ----------
  const bgStyle: React.CSSProperties = !backgroundClassName && resolvedPrimaryColor
    ? { backgroundColor: resolvedPrimaryColor }
    : {};
  const bgClass = backgroundClassName || (resolvedPrimaryColor ? '' : 'bg-gradient-to-r from-blue-500 to-indigo-600');

  // ==========================================================================
  // RENDER: TYPE FILTER TABS (pill-toggle row inside coloured bar)
  // ==========================================================================
  const renderPillBar = (
    tabs: TypeFilterTab[],
    options: {
      showAll?: boolean;
      activeFilter?: string | null;
      onChange?: (id: string | null) => void;
    } = {}
  ) => {
    const resolvedActive = options.activeFilter !== undefined ? options.activeFilter : activeTypeFilter;
    const resolvedOnChange = options.onChange ?? handleTypeFilterChange;
    return (
    <div
      className={cn(
        'flex items-center gap-0 bg-white/10 overflow-x-auto scrollbar-thin scrollbar-thumb-white/30',
        isDense ? 'rounded-lg p-0.5' : 'rounded-xl p-1'
      )}
    >
      {options.showAll && (
        <button
          onClick={() => resolvedOnChange?.(null)}
          className={cn(
            'flex items-center justify-center rounded-lg font-medium whitespace-nowrap transition-colors',
            isDense ? 'gap-1 px-2.5 py-1 text-[13px]' : 'gap-2 px-4 py-1.5 text-sm',
            resolvedActive === null
              ? 'bg-white text-gray-800 shadow-lg'
              : 'text-white/90 hover:bg-white/20'
          )}
        >
          All
        </button>
      )}
      {tabs.map((tab) => {
        const TabIcon = tab.icon;
        const isActive = resolvedActive === tab.id;
        return (
          <React.Fragment key={tab.id}>
            {tab.separatorBefore && (
              <div className="w-px mx-0.5 self-stretch bg-white/20 flex-shrink-0" />
            )}
            <button
              onClick={() => resolvedOnChange?.(isActive ? null : tab.id)}
              data-tour-id={tab.dataTourId}
              className={cn(
                'flex items-center justify-center rounded-lg font-medium whitespace-nowrap transition-colors relative',
                isDense ? 'gap-1 px-2.5 py-1 text-[13px]' : 'gap-2 px-4 py-1.5 text-sm',
                isActive
                  ? 'bg-white text-gray-800 shadow-lg'
                  : 'text-white/90 hover:bg-white/20',
                tab.highlighted && isActive
                  ? 'ring-2 ring-amber-200 shadow-[0_0_0_2px_rgba(254,240,138,0.35)]'
                  : '',
                tab.highlighted && !isActive
                  ? 'bg-white/15 ring-2 ring-amber-200 shadow-[0_0_0_2px_rgba(254,240,138,0.35)]'
                  : ''
              )}
            >
              {TabIcon && (
                <TabIcon className={cn('flex-shrink-0', isDense ? 'w-3 h-3' : 'w-4 h-4')} />
              )}
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.accentColor && isActive && (
                <span
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[3px] rounded-full"
                  style={{ backgroundColor: tab.accentColor, width: '60%' }}
                />
              )}
            </button>
          </React.Fragment>
        );
      })}
    </div>
    );
  };

  const renderTypeFilterTabs = () => {
    if (!hasTypeFilters) return centerContent || null;
    return renderPillBar(typeFilterTabs!, { showAll: showAllTab });
  };

  // ==========================================================================
  // RENDER: STAT PILLS (gradient badges — Open 56, Urgent 10, etc.)
  // ==========================================================================
  const renderStatPills = () => {
    if (!showStatPills) return null;
    const visiblePills = statPills!.filter(p => {
      if (p.hideWhenZero && (p.value === 0 || p.value === '0')) return false;
      return true;
    });
    if (visiblePills.length === 0) return null;

    return (
      <div className={cn('flex flex-wrap items-center justify-between', isDense ? 'gap-2' : 'gap-3')}>
        {visiblePills.map((pill) => {
          const PillIcon = pill.icon;
          return (
            <button
              key={pill.id}
              onClick={pill.onClick}
              className={cn(
                'flex items-center rounded-xl text-white shadow-sm transition-transform hover:scale-105',
                isDense ? 'gap-1 px-2.5 py-1' : 'gap-2 px-3.5 py-2',
                pill.gradient ? `bg-gradient-to-r ${pill.gradient}` : '',
                pill.className
              )}
            >
              {PillIcon && <PillIcon className={cn(isDense ? 'w-3 h-3' : 'w-4 h-4')} />}
              <span className={cn('font-medium', isDense ? 'text-[11px] sm:text-xs' : 'text-sm')}>{pill.label}</span>
              <span className={cn('font-bold', isDense ? 'text-sm sm:text-base' : 'text-lg')}>{pill.value}</span>
            </button>
          );
        })}
      </div>
    );
  };

  // ==========================================================================
  // RENDER: STAT CARDS (6 cards in a row — dashboard variant)
  // ==========================================================================
  const renderStatCards = () => {
    if (!showStatCards) return null;
    return (
      <div className={cn('grid grid-cols-2 sm:grid-cols-3', statCardGridClass, useCompactStatCards ? 'mt-1.5 gap-2' : 'mt-3 gap-3')}>
        {statCards!.map((card) => {
          const CardIcon = card.icon;
          return (
            <button
              key={card.id}
              onClick={card.onClick}
              aria-pressed={card.active ? true : undefined}
              data-tour-id={card.dataTourId}
              className={cn(
                'relative flex items-center backdrop-blur-sm rounded-lg transition-all text-left',
                card.active
                  ? 'bg-white text-slate-900 ring-2 ring-white shadow-xl scale-[1.015]'
                  : 'bg-white/15 hover:bg-white/25',
                useCompactStatCards ? 'gap-2 px-2.5 py-2' : 'gap-3 px-4 py-3'
              )}
            >
              {card.active && (
                <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-700 shadow-sm">
                  <Check className="h-3 w-3" />
                  <span className="hidden sm:inline">Filtered</span>
                </span>
              )}
              {CardIcon && (
                <div className={cn(
                  'rounded-lg flex items-center justify-center flex-shrink-0 transition-colors',
                  card.active ? 'bg-slate-100' : 'bg-white/20',
                  useCompactStatCards ? 'w-7 h-7' : 'w-9 h-9'
                )}>
                  <CardIcon className={cn(card.active ? 'text-slate-700' : 'text-white', useCompactStatCards ? 'w-3.5 h-3.5' : 'w-5 h-5')} />
                </div>
              )}
              <div className={cn('min-w-0 flex-1', useCompactStatCards ? 'pr-12 sm:pr-14' : 'pr-14 sm:pr-16')}>
                <div className={cn(card.active ? 'text-slate-500' : 'text-white/70', 'font-medium truncate', useCompactStatCards ? 'text-[10px] sm:text-[11px]' : 'text-xs')}>
                  {card.label}
                </div>
                <div className={cn(card.active ? 'text-slate-900' : 'text-white', 'font-bold leading-tight', useCompactStatCards ? 'text-sm sm:text-base' : 'text-lg')}>
                  {card.value}
                </div>
                {card.subtitle && (
                  <div className={cn(card.active ? 'text-slate-500' : 'text-white/60', 'truncate', useCompactStatCards ? 'text-[10px] sm:text-[11px]' : 'text-xs')}>
                    {card.subtitle}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  // ==========================================================================
  // RENDER: ACTION BUTTONS (right side of title bar)
  // ==========================================================================
  const renderActions = () => (
    <div className={cn('flex items-center', isDense ? 'gap-1' : 'gap-2')}>
      {allActions.map((action, index) => {
        const ActionIcon = action.icon;
        const showLabel = action.showLabel === 'always' ||
          (action.showLabel === 'expanded' && !minimized);

        const isSolidVariant = action.variant === 'solid' || action.variant === 'primary';

        if (isSolidVariant) {
          return (
            <button
              key={action.id ?? action.label ?? index}
              onClick={action.onClick}
              disabled={action.disabled || action.loading}
              className={cn(
                'flex items-center gap-1.5 bg-white text-gray-800 rounded-lg font-medium hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50',
                isDense ? 'px-2.5 py-1.5 text-[13px]' : 'px-4 py-2 text-sm'
              )}
              title={action.title || action.label}
              data-tour-id={action.dataTourId}
            >
              <ActionIcon className={cn(isDense ? 'w-3.5 h-3.5' : 'w-4 h-4', action.loading && 'animate-spin')} />
              {showLabel && action.label && (
                <span className="hidden sm:inline">{action.label}</span>
              )}
            </button>
          );
        }

        return (
          <button
            key={action.id ?? action.label ?? index}
            onClick={action.onClick}
            disabled={action.disabled || action.loading}
            className={cn(
              'rounded-lg bg-white/20 hover:bg-white/30 transition-colors flex items-center gap-1.5 disabled:opacity-50',
              isDense ? 'px-2 py-1.5' : 'p-2'
            )}
            title={action.title || action.label}
            data-tour-id={action.dataTourId}
          >
            <ActionIcon className={cn(isDense ? 'w-3.5 h-3.5 text-white' : 'w-5 h-5 text-white', action.loading && 'animate-spin')} />
            {showLabel && action.label && (
              <span className={cn('text-white font-medium hidden sm:inline', isDense ? 'text-[13px]' : 'text-sm')}>{action.label}</span>
            )}
          </button>
        );
      })}

      {/* New button — solid white pill on right */}
      {showNewButton && onNew && (
        <button
          onClick={onNew}
          data-tour-id={newButtonDataTourId}
          className={cn(
            'flex items-center gap-1.5 bg-white text-gray-800 rounded-lg font-medium hover:bg-gray-50 transition-colors shadow-sm',
            isDense ? 'px-2.5 py-1.5 text-[13px]' : 'px-4 py-2 text-sm'
          )}
        >
          <Plus className={cn(isDense ? 'w-3.5 h-3.5' : 'w-4 h-4')} />
          <span className="hidden sm:inline">{newButtonLabel}</span>
        </button>
      )}
    </div>
  );

  // ==========================================================================
  // MAIN RENDER
  // ==========================================================================

  return (
    <div
      className={cn(
        'relative flex-shrink-0 text-white transition-all duration-200',
        bgClass,
        className
      )}
      style={bgStyle}
    >
      {/* ================================================================ */}
      {/* TITLE BAR — always visible                                       */}
      {/* ================================================================ */}
      <div
        className={cn(
          'flex flex-col sm:flex-row sm:items-center sm:justify-between transition-all duration-200',
          isDense ? 'gap-1 sm:gap-2' : 'gap-2 sm:gap-4',
          // Compact variant when minimized shrinks padding
          isCompactMinimized ? 'px-2.5 py-1' : isDense ? 'px-2.5 py-1.5 sm:py-1.5' : 'px-4 py-3 sm:py-4'
        )}
      >
        {/* Far-left: Nav layout toggle (white icon, same row as pills) */}
        {showCombinedLayoutToggle && (
          <button
            onClick={toggleNavLayoutMode}
            className={cn(
              'flex items-center justify-center flex-shrink-0 rounded-lg transition-all',
              isDense ? 'h-7 w-7' : 'h-8 w-8 sm:h-9 sm:w-9',
              'text-white/70 hover:text-white hover:bg-white/10',
            )}
            title={isVerticalNav ? 'Switch to horizontal navigation (pills in header)' : 'Switch to vertical navigation (sidebar)'}
          >
            {isVerticalNav ? (
              <PanelLeft className={cn(isDense ? 'h-4 w-4' : 'h-4.5 w-4.5 sm:h-5 sm:w-5')} />
            ) : (
              <PanelTop className={cn(isDense ? 'h-4 w-4' : 'h-4.5 w-4.5 sm:h-5 sm:w-5')} />
            )}
          </button>
        )}

        {/* Left: Icon + Title + Subtitle */}
        <div className={cn('flex items-center min-w-0', isDense ? 'gap-1.5 sm:gap-2' : 'gap-2 sm:gap-3')}>
          <Icon
            className={cn(
              'text-white/90 flex-shrink-0 transition-all',
              isCompactMinimized ? 'w-4 h-4' : isDense ? 'h-[18px] w-[18px] sm:h-5 sm:w-5' : 'w-7 h-7 sm:w-8 sm:h-8'
            )}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h1
                className={cn(
                  'font-bold text-white transition-all truncate',
                  isCompactMinimized ? 'text-[13px]' : isDense ? 'text-[15px] sm:text-base' : 'text-xl sm:text-2xl'
                )}
              >
                {displayTitle}
              </h1>
              {favouriteConfig && !isCompactMinimized && (
                <button
                  onClick={handleToggleFavourite}
                  className={cn(
                    'flex-shrink-0 rounded-md p-0.5 transition-all',
                    isFav
                      ? 'text-amber-300 hover:text-amber-200'
                      : 'text-white/85 hover:text-white'
                  )}
                  title={isFav ? 'Remove from favourites' : 'Add to favourites'}
                >
                  <Star className={cn(
                    'transition-all',
                    isDense ? 'h-3.5 w-3.5' : 'h-4 w-4',
                    isFav ? 'fill-amber-300' : ''
                  )} />
                </button>
              )}
              {titleExtra}
            </div>
            {!isCompactMinimized && subtitle && (
              <p className={cn(
                'text-white/80 hidden sm:block truncate',
                isDense ? 'mt-0 text-[11px] sm:text-xs leading-tight' : 'mt-0.5 text-sm'
              )}>
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Center: Type filter tabs OR custom center content — hidden in vertical nav mode */}
        {!isCompactMinimized && !isVerticalNav && (
          <div className="flex items-center justify-center flex-shrink-0">
            {renderTypeFilterTabs()}
          </div>
        )}

        {/* Right: Actions + view toggles */}
        <div className={cn(
          'flex flex-col',
          hasViewToggles && !isCompactMinimized ? (isDense ? 'gap-1 sm:items-end' : 'gap-1.5 sm:items-end') : 'sm:items-end'
        )}>
          {renderActions()}
          {!isCompactMinimized && viewToggles && (
            <div className="flex items-center sm:justify-end">
              {viewToggles}
            </div>
          )}
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECONDARY TYPE FILTER ROW — used when primary row is too full     */}
      {/* Sits directly below the title bar, full-width, centered pills.    */}
      {/* Rendered below stat cards when secondaryFiltersBelowStats is true. */}
      {/* ================================================================ */}
      {hasSecondaryTypeFilters && !secondaryFiltersBelowStats && !isCompactMinimized && !isVerticalNav && (
        <div
          className={cn(
            'flex justify-center',
            isDense ? 'px-2.5 pb-1.5 -mt-0.5' : 'px-4 pb-2 -mt-1'
          )}
        >
          {renderPillBar(typeFilterTabsSecondary!, {
            showAll: false,
            activeFilter: activeTypeFilterSecondary !== undefined ? activeTypeFilterSecondary : activeTypeFilter,
            onChange: onTypeFilterSecondaryChange ? handleTypeFilterSecondaryChange : handleTypeFilterChange,
          })}
        </div>
      )}

      {/* ================================================================ */}
      {/* EXPANDABLE SECTION — stat pills, stat cards, bottom slot          */}
      {/* Hidden when minimized for dashboard & with-stats variants.        */}
      {/* Dashboard variant keeps ONLY the stat-cards row in vertical nav.  */}
      {/* ================================================================ */}
      {(!isCollapsedContent || showDashboardStatsInVerticalNav) && !isCompactMinimized && (
        <>
          {/* Stat pills row */}
          {showStatPills && !isCollapsedContent && (
            <div className={cn(isDense ? 'px-3 pb-2' : 'px-4 pb-3')}>
              {renderStatPills()}
            </div>
          )}

          {/* Stat cards row (dashboard variant — stays visible in vertical nav) */}
          {showStatCards && (
            <div className={cn(useCompactStatCards ? 'px-3 pb-2' : isDense ? 'px-3 pb-2' : 'px-4 pb-3')}>
              {renderStatCards()}
            </div>
          )}

          {/* Bottom slot */}
          {bottomSlot && !isCollapsedContent && (
            <div className={cn(isDense ? 'px-3 pb-2 pt-0.5' : 'px-4 pb-3 pt-1')}>
              {bottomSlot}
            </div>
          )}

          {/* Secondary type filter row — rendered below stat cards when secondaryFiltersBelowStats is true */}
          {hasSecondaryTypeFilters && secondaryFiltersBelowStats && !isCollapsedContent && (
            <div
              className={cn(
                'flex justify-center',
                isDense ? 'px-2.5 pb-2 pt-0.5' : 'px-4 pb-3 pt-1'
              )}
            >
              {renderPillBar(typeFilterTabsSecondary!, {
                showAll: false,
                activeFilter: activeTypeFilterSecondary !== undefined ? activeTypeFilterSecondary : activeTypeFilter,
                onChange: onTypeFilterSecondaryChange ? handleTypeFilterSecondaryChange : handleTypeFilterChange,
              })}
            </div>
          )}
        </>
      )}

      {/* ================================================================ */}
      {/* PERSISTENT BOTTOM SLOT — always visible even when minimized       */}
      {/* ================================================================ */}
      {persistentBottomSlot && !isCompactMinimized && (
        <div className={cn(isDense ? 'px-2.5 pb-1.5 pt-0' : 'px-4 pb-3 pt-1')}>
          {persistentBottomSlot}
        </div>
      )}

      {showCombinedLayoutToggle && (
        <div className="absolute -bottom-2.5 left-2 hidden lg:flex lg:items-center lg:gap-0.5 z-20">
          <button
            onClick={toggleCombinedLayout}
            className={cn(SHELL_TOGGLE_BUTTON_CLASS)}
            title={minimized && sideNavCollapsed ? 'Expand header and sidebar' : 'Minimize header and sidebar'}
          >
            {minimized && sideNavCollapsed ? (
              <ArrowDownRight className={SHELL_TOGGLE_ICON_CLASS} />
            ) : (
              <ArrowUpLeft className={SHELL_TOGGLE_ICON_CLASS} />
            )}
          </button>
        </div>
      )}

      {/* ================================================================ */}
      {/* HELP / TOUR ICON — bottom right, above minimize toggle            */}
      {/* ================================================================ */}
      {(onHelpClick || componentTourConfig) && !isCompactMinimized && (
        <button
          onClick={() => {
            if (onHelpClick) {
              onHelpClick();
            } else if (componentTourConfig && typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('guided-tour-open', {
                  detail: {
                    moduleKey: componentTourConfig.moduleKey,
                    flowId: componentTourConfig.flowId,
                    stepId: componentTourConfig.stepId,
                    objectiveId: componentTourConfig.objectiveId,
                  },
                })
              );
            }
          }}
          className={cn(
            'absolute bottom-8 right-3 z-10',
            SHELL_TOGGLE_BUTTON_CLASS,
          )}
          title={helpButtonTooltip || componentTourConfig?.tooltip || 'Get help with this section'}
          aria-label={helpButtonTooltip || componentTourConfig?.tooltip || 'Get help with this section'}
        >
          <HelpCircle className={SHELL_TOGGLE_ICON_CLASS} />
        </button>
      )}

      {/* ================================================================ */}
      {/* CHEVRON — white circle protruding from bottom (like SideNav)      */}
      {/* ================================================================ */}
      <button
        onClick={toggleMinimized}
        className={cn(
          'absolute -bottom-2.5 right-3 z-10',
          SHELL_TOGGLE_BUTTON_CLASS,
        )}
        title={minimized ? 'Expand header' : 'Minimize header'}
      >
        {minimized ? (
          <ChevronDown className={SHELL_TOGGLE_ICON_CLASS} />
        ) : (
          <ChevronUp className={SHELL_TOGGLE_ICON_CLASS} />
        )}
      </button>
    </div>
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

export { ModuleHeader };
